import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Home, Package, Users, Images, CreditCard, LogOut, ShieldCheck, Sparkles, Mail, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import SalespersonUsage from "@/pages/dashboard/SalespersonUsage";

const nav = [
  { to: "/dashboard", end: true, label: "Overview", icon: Home, tid: "nav-overview" },
  { to: "/dashboard/products", label: "Product Catalog", icon: Package, tid: "nav-products" },
  { to: "/dashboard/sales-team", label: "Sales Team", icon: Users, tid: "nav-sales-team" },
  { to: "/dashboard/history", label: "History", icon: Images, tid: "nav-history" },
  { to: "/dashboard/billing", label: "Credits & Billing", icon: CreditCard, tid: "nav-billing" },
  { to: "/dashboard/contact", label: "Contact Us", icon: Mail, tid: "nav-contact" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer on navigation
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const navLinks = (
    <nav className="mt-12 flex-1 space-y-1">
      {nav.map(({ to, end, label, icon: Icon, tid }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-testid={tid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${
              isActive
                ? "bg-[#F3F1EC] text-[#1A1C1E] font-medium"
                : "text-[#5C5F62] hover:text-[#1A1C1E] hover:bg-[#F3F1EC]/50"
            }`
          }
        >
          <Icon className="h-4 w-4" strokeWidth={1.5} />
          {label}
        </NavLink>
      ))}
      {user?.role === "admin" && (
        <NavLink to="/admin" data-testid="nav-admin" className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[#5C5F62] hover:text-[#1A1C1E] hover:bg-[#F3F1EC]/50 transition">
          <ShieldCheck className="h-4 w-4" strokeWidth={1.5} /> Admin Panel
        </NavLink>
      )}
    </nav>
  );

  const accountBlock = (
    <div className="mt-8 border-t border-[#E5E5E5] pt-6">
      <p className="text-xs uppercase tracking-wider text-[#8A8D91]">Signed in as</p>
      <p className="mt-1 text-sm font-medium truncate">{user?.business_name}</p>
      <p className="text-xs text-[#8A8D91] truncate">{user?.email}</p>
      <button data-testid="logout-button" onClick={onLogout} className="mt-4 inline-flex items-center gap-2 text-sm text-[#5C5F62] hover:text-[#B74F4F] transition">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-[260px] lg:flex-col border-r border-[#E5E5E5] bg-white p-6">
        <Logo />
        {navLinks}
        {accountBlock}
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[80vw] flex-col overflow-y-auto bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <Logo />
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="rounded-md p-2 text-[#5C5F62] hover:bg-[#F3F1EC]"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {navLinks}
            {accountBlock}
          </aside>
        </div>
      )}

      <main className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 border-b border-[#E5E5E5] bg-white/70 backdrop-blur-md">
          <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-6 md:px-12 md:py-5">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="-ml-2 rounded-md p-2 text-[#5C5F62] hover:bg-[#F3F1EC] lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="hidden truncate text-sm text-[#8A8D91] sm:block">Retailer dashboard</div>
            </div>
            <div className="flex shrink-0 items-center gap-2 sm:gap-4">
              <button
                data-testid="visualize-button"
                onClick={() => navigate("/visualize")}
                className="neivai-btn-primary inline-flex items-center gap-1.5 whitespace-nowrap px-3 text-xs sm:gap-2 sm:px-4 sm:text-sm"
              >
                <Sparkles className="h-4 w-4" /> Visualize
              </button>
              <div data-testid="credits-badge" className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#F3F1EC] px-2.5 py-1.5 text-xs sm:gap-2 sm:px-3 sm:text-sm">
                <span className="h-2 w-2 rounded-full bg-[#C86B53]" />
                <span className="font-medium">{user?.credits ?? 0}</span>
                <span className="text-[#5C5F62]">credits</span>
              </div>
            </div>
          </div>
        </header>
        <div className="px-4 py-6 sm:px-6 md:px-12 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
